import { useState, useCallback } from 'react';

import get from 'lodash/get';

import {
  IOktaError,
  TLoginState,
  TOktaApiResponse,
  TOktaMFAFactor,
  TOktaApiSMSFactor,
  TOktaApiEmailFactor,
} from '../interfaces';

import api from './api';

export type OktaLoginViewProps = {
  deviceToken: string;
  handleSuccess: (data: Partial<TOktaApiResponse>) => void;
  initialView?: TLoginState;
};

const getEmptyMfaUrlState = () => ({
  email: '',
  sms: '',
});

export function useOktaLoginView({
  deviceToken,
  handleSuccess,
  initialView = 'LOGIN',
}: OktaLoginViewProps) {
  const [stateToken, setStateToken] = useState<string | null>(null);
  const [oktaApiUrls, setOktaApiUrls] = useState(() => {
    return {
      activate: '',
      enroll: getEmptyMfaUrlState(),
      resend: getEmptyMfaUrlState(),
      resetPassword: '',
      verify: getEmptyMfaUrlState(),
    };
  });
  const [currentLoginState, setCurrentLoginState] =
    useState<TLoginState>(initialView);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorView, setErrorView] = useState<TLoginState | null>(null);

  function findPhone(embedded: TOktaApiResponse['_embedded']) {
    const isSmsFactor = (
      f: TOktaApiSMSFactor | TOktaApiEmailFactor
    ): f is TOktaApiSMSFactor => 'phoneNumber' in f.profile;
    const smsFactor = embedded.factors.find(isSmsFactor);

    return smsFactor?.profile?.phoneNumber;
  }

  function getFactorLinks(
    embedded: TOktaApiResponse['_embedded'],
    key: 'enroll' | 'verify'
  ) {
    return embedded.factors.reduce(
      (acc, factor) => ({
        ...acc,
        [factor.factorType]: factor._links[key].href,
      }),
      {
        email: '',
        sms: '',
      }
    );
  }

  function getResendLink(_links: TOktaApiResponse['_links']) {
    return (_links?.resend || []).reduce(
      (acc, link) => ({
        ...acc,
        [link.name!]: link.href,
      }),
      {
        email: '',
        sms: '',
      }
    );
  }

  function onSuccess({
    status,
    stateToken,
    _embedded,
    _links,
    sessionToken,
  }: TOktaApiResponse) {
    switch (status) {
      case 'MFA_REQUIRED':
        setCurrentLoginState('MFA_SEND_CODE');
        setStateToken(stateToken);
        setOktaApiUrls((currentOktaApiUrls) => ({
          ...currentOktaApiUrls,
          verify: getFactorLinks(_embedded, 'verify'),
        }));
        const phone = findPhone(_embedded);
        if (phone) {
          setPhoneNumber(phone);
        }
        setIsSubmitting(false);
        break;
      case 'MFA_ENROLL':
        setCurrentLoginState('MFA_ENROLL');
        setStateToken(stateToken);
        setOktaApiUrls((currentOktaApiUrls) => ({
          ...currentOktaApiUrls,
          enroll: getFactorLinks(_embedded, 'enroll'),
        }));
        setIsSubmitting(false);
        break;
      case 'PASSWORD_RESET':
        setCurrentLoginState('PASSWORD_RESET');
        setStateToken(stateToken);
        setOktaApiUrls((currentOktaApiUrls) => ({
          ...currentOktaApiUrls,
          resetPassword: get(_links, 'next.href'),
        }));
        setIsSubmitting(false);
        break;
      case 'SUCCESS':
        handleSuccess({
          status,
          stateToken,
          sessionToken,
        });
        break;
      default:
        setIsSubmitting(false);
    }
  }

  function authenticate(username: string, password: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaAuthenticate({
          username,
          password,
          options: {
            multiOptionalFactorEnroll: false,
          },
          context: {
            deviceToken,
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function sendCode(factor: TOktaMFAFactor) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.verify[factor],
          opts: {
            passCode: '',
            stateToken,
            context: {
              deviceToken,
            },
          },
        })
        .then(({ _links }) => {
          setIsSubmitting(false);
          setCurrentLoginState('MFA_VERIFY_CODE');
          setOktaApiUrls((currentOktaApiUrls) => ({
            ...currentOktaApiUrls,
            resend: getResendLink(_links),
          }));
          resolve({});
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  function verifyCode(
    passCode: string,
    factor: TOktaMFAFactor,
    opts?: { shouldRememberDevice: boolean }
  ) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: `${oktaApiUrls.verify[factor]}${
            opts?.shouldRememberDevice ? '?rememberDevice=true' : ''
          }`,
          opts: {
            passCode,
            stateToken,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setErrorView(currentLoginState);
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  function resendCode(factor: TOktaMFAFactor) {
    if (errorView) {
      setErrorView(null);
    }
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.resend[factor],
          opts: {
            stateToken,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          setIsSubmitting(false);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  function resetPassword(username: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaResetPassword({
          username,
          factorType: 'EMAIL',
          context: {
            deviceToken,
          },
        })
        .then((data) => {
          setIsSubmitting(false);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  const validateRecoveryToken = useCallback((recoveryToken: string) => {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaValidateRecoveryToken({
          recoveryToken,
          context: {
            deviceToken,
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView('PASSWORD_RESET');
          reject(error);
        });
    });
  }, []);

  function updatePassword(newPassword: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.resetPassword,
          opts: {
            stateToken,
            newPassword,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function enrollMFAEmail(email: string) {
    return enrollMFAImplementation({
      url: oktaApiUrls.enroll.email,
      opts: {
        stateToken,
        factorType: 'email',
        provider: 'OKTA',
        profile: {
          email,
        },
        context: {
          deviceToken,
        },
      },
    });
  }

  function enrollMFASms(phoneNumber: string) {
    return enrollMFAImplementation({
      url: `${oktaApiUrls.enroll.sms}?updatePhone=true`,
      opts: {
        stateToken,
        factorType: 'sms',
        provider: 'OKTA',
        profile: {
          phoneNumber: phoneNumber.replace('+', ''),
        },
        context: {
          deviceToken,
        },
      },
    }).then(() => {
      setPhoneNumber(phoneNumber);
      return {};
    });
  }

  function enrollMFAImplementation(requestOptions: {
    url: string;
    opts: Record<string, any>;
  }) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest(requestOptions)
        .then(({ _links }) => {
          setIsSubmitting(false);
          setCurrentLoginState('MFA_ACTIVATE');
          setOktaApiUrls((currentOktaApiUrls) => ({
            ...currentOktaApiUrls,
            activate: get(_links, 'next.href'),
            resend: getResendLink(_links),
          }));
          resolve({});
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function enrollMFA(phoneNumberOrEmail: string) {
    return phoneNumberOrEmail.includes('@')
      ? enrollMFAEmail(phoneNumberOrEmail)
      : enrollMFASms(phoneNumberOrEmail);
  }

  function activateMFA(passCode: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.activate,
          opts: {
            stateToken,
            passCode,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function toggleForgotPassword() {
    if (currentLoginState === 'LOGIN') {
      setCurrentLoginState('FORGOT_PASSWORD');
    } else {
      setCurrentLoginState('LOGIN');
    }
  }

  const getOktaStateToken = useCallback(
    (
      handleRedirect: () => void,
      oktaLoginInfo?: {
        username: string;
        password: string;
      }
    ) => {
      if (oktaLoginInfo) {
        api
          .oktaAuthenticate({
            username: oktaLoginInfo.username,
            password: oktaLoginInfo.password,
            options: {
              multiOptionalFactorEnroll: false,
            },
            context: {
              deviceToken,
            },
          })
          .then(({ status, stateToken, _embedded }) => {
            if (status === 'MFA_ENROLL') {
              setStateToken(stateToken);
              setOktaApiUrls((currentOktaApiUrls) => {
                return {
                  ...currentOktaApiUrls,
                  enroll: getFactorLinks(_embedded, 'enroll'),
                };
              });
            } else {
              handleRedirect();
            }
          });
      } else {
        handleRedirect();
      }
    },
    []
  );

  return {
    currentLoginState,
    phoneNumber,
    isSubmitting,
    errorView,
    authenticate,
    sendCode,
    verifyCode,
    resendCode,
    resetPassword,
    enrollMFA,
    activateMFA,
    toggleForgotPassword,
    getOktaStateToken,
    validateRecoveryToken,
    updatePassword,
  };
}
